import {SupportedLocales} from '#types';

export const sourceConnectionsLang = {
  [SupportedLocales.EN]: {
    title: 'Datasets',
    subtitle: 'View and manage datasets for your audiences.',
    tabs: {
      datasetGroups: {
        label: 'Dataset Groups',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'group' : 'groups'}`
            : 'No groups set',
        heading: 'Dataset Groups',
        description:
          "What you build your Audiences on. It's a way to combine sources of data to later segment your audience based off of.",
        create: 'New Dataset Group',
      },
      datasets: {
        label: 'Datasets',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'item' : 'items'}`
            : 'No items set',
        heading: 'Datasets',
        description:
          'These are representations of the tables in your data warehouse. It’s where you add more context to each underlying data source.',
        create: 'New Dataset',
      },
      sourceConnections: {
        label: 'Source Connections',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'source connection' : 'source connections'}`
            : 'No source connections set',
        heading: 'Source Connections',
        description: 'Available connections to your data cloud.',
      },
      queueEvents: {
        label: 'Realtime Events',
        count: (count: number) => {
          if (count === 0) return 'No Events';
          if (count === 1) return '1 Event';
          return `${count} Events`;
        },
        heading: 'Realtime Events',
        description: 'Events streamed from your event queues.',
        create: 'New Realtime Events',
      },
    },
    datasetGroupsTable: {
      ariaLabel: 'Dataset groups table',
      search: 'Search dataset groups',
      name: 'Name',
      sourceConnection: 'Source Connection',
      primaryDataset: 'Primary Dataset',
      otherDatasets: 'Other Datasets',
      audiences: 'Audiences',
      options: 'Options',
      edit: 'Edit dataset group',
      addExisting: 'Add existing dataset',
      delete: 'Delete dataset group',
      empty: 'There are no dataset groups.',
      emptySearch: 'There are no dataset groups matching your search.',
      loadFailed: 'Dataset groups could not be loaded.',
      tryAgain: 'Try again',
      deleteTitle: 'Delete dataset group?',
      deleteDescription: (name: string) =>
        `This permanently deletes “${name}”.`,
      deleteConfirm: 'Delete',
      deleteCancel: 'Cancel',
      deleteSuccess: 'Dataset group deleted',
      deleteFailed: 'The dataset group could not be deleted.',
    },
    datasetsTable: {
      ariaLabel: 'Datasets table',
      search: 'Search datasets',
      filterByGroup: 'Filter by group',
      filterByGroupSearch: 'Search dataset groups',
      filterByGroupEmpty: 'There are no dataset groups to filter with.',
      name: 'Name',
      sourceConnection: 'Source Connection',
      type: 'Type',
      description: 'Description',
      audiences: 'Audiences',
      activeExports: 'Active Exports',
      syncs: 'Syncs',
      groups: 'Groups',
      status: 'Status',
      lastUpdated: 'Last updated',
      createdAt: 'Created At',
      options: 'Options',
      edit: 'Edit Dataset',
      refreshFields: 'Refresh Fields',
      delete: 'Delete Dataset',
      empty: 'There are no datasets.',
      emptySearch: 'There are no datasets matching your search.',
      loadFailed: 'Datasets could not be loaded.',
      tryAgain: 'Try again',
      emptyValue: '-',
      deleteTitle: 'Delete dataset?',
      deleteDescription: (name: string) =>
        `This permanently deletes “${name}”.`,
      deleteConfirm: 'Delete',
      deleteCancel: 'Cancel',
      deleteSuccess: 'Dataset deleted',
      deleteFailed: 'The dataset could not be deleted.',
      refreshSuccess: (name: string) =>
        `Dataset cache successfully cleared for “${name}.”`,
      refreshFailed: 'The dataset cache could not be cleared.',
      lastUpdatedUnavailable: 'not available',
      lastUpdatedView: 'not applicable for views',
      lastUpdatedViewTooltip:
        'This dataset is defined as a view and reads live from its source tables. Last updated is only shown for base tables and materialized views.',
    },
    sourceConnectionsTable: {
      ariaLabel: 'Source connections table',
      search: 'Search source connections',
      id: 'Id',
      name: 'Name',
      type: 'Type',
      description: 'Description',
      teams: 'Teams',
      datasetGroups: 'Dataset Groups',
      lastSchemaRefresh: 'Last Schema Refresh',
      schemaRefreshInProgress: 'Schema refresh in progress',
      schemaRefreshFailed: 'Last schema refresh failed',
      schemaRefreshTrigger: 'Trigger the schema sync job',
      schemaRefreshTriggered:
        "We've triggered the schema sync job. Please note this might take a few minutes",
      schemaRefreshSubmitted: 'Refresh Schema submitted',
      schemaRefreshSubmitFailed: 'The schema refresh could not be started.',
      status: 'Status',
      active: 'Active',
      createdAt: 'Created At',
      empty: 'There are no source connections.',
      emptySearch: 'There are no source connections matching your search.',
      loadFailed: 'Source connections could not be loaded.',
      tryAgain: 'Try again',
      emptyValue: '-',
    },
    queueEventsTable: {
      ariaLabel: 'Realtime events table',
      empty: 'There are no realtime events.',
      emptySearch: 'There are no realtime events matching your search.',
      loadFailed: 'Realtime events could not be loaded.',
      tryAgain: 'Try again',
    },
  },
  [SupportedLocales.ES]: {
    title: 'Conjuntos de datos',
    subtitle: 'Consulta y administra conjuntos de datos para tus audiencias.',
    tabs: {
      datasetGroups: {
        label: 'Grupos de conjuntos de datos',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'grupo' : 'grupos'}`
            : 'No hay grupos',
        heading: 'Grupos de conjuntos de datos',
        description:
          'Sobre lo que construyes tus audiencias. Es una forma de combinar fuentes de datos para segmentar tu audiencia después.',
        create: 'Nuevo grupo de conjuntos de datos',
      },
      datasets: {
        label: 'Conjuntos de datos',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'elemento' : 'elementos'}`
            : 'No hay elementos',
        heading: 'Conjuntos de datos',
        description:
          'Representaciones de las tablas en tu almacén de datos. Aquí añades más contexto a cada fuente de datos subyacente.',
        create: 'Nuevo conjunto de datos',
      },
      sourceConnections: {
        label: 'Conexiones de origen',
        count: (count: number) =>
          count > 0
            ? `${count} ${count === 1 ? 'conexión de origen' : 'conexiones de origen'}`
            : 'No hay conexiones de origen',
        heading: 'Conexiones de origen',
        description: 'Conexiones disponibles a tu nube de datos.',
      },
      queueEvents: {
        label: 'Eventos en tiempo real',
        count: (count: number) => {
          if (count === 0) return 'Sin eventos';
          if (count === 1) return '1 evento';
          return `${count} eventos`;
        },
        heading: 'Eventos en tiempo real',
        description: 'Eventos transmitidos desde tus colas de eventos.',
        create: 'Nuevos eventos en tiempo real',
      },
    },
    datasetGroupsTable: {
      ariaLabel: 'Tabla de grupos de conjuntos de datos',
      search: 'Buscar grupos',
      name: 'Nombre',
      sourceConnection: 'Conexión de origen',
      primaryDataset: 'Conjunto de datos principal',
      otherDatasets: 'Otros conjuntos de datos',
      audiences: 'Audiencias',
      options: 'Opciones',
      edit: 'Editar grupo',
      addExisting: 'Agregar conjunto de datos existente',
      delete: 'Eliminar grupo',
      empty: 'No hay grupos de conjuntos de datos.',
      emptySearch: 'No hay grupos que coincidan con tu búsqueda.',
      loadFailed: 'No se pudieron cargar los grupos.',
      tryAgain: 'Reintentar',
      deleteTitle: '¿Eliminar grupo?',
      deleteDescription: (name: string) =>
        `Esto elimina permanentemente “${name}”.`,
      deleteConfirm: 'Eliminar',
      deleteCancel: 'Cancelar',
      deleteSuccess: 'Grupo eliminado',
      deleteFailed: 'No se pudo eliminar el grupo.',
    },
    datasetsTable: {
      ariaLabel: 'Tabla de conjuntos de datos',
      search: 'Buscar conjuntos de datos',
      filterByGroup: 'Filtrar por grupo',
      filterByGroupSearch: 'Buscar grupos',
      filterByGroupEmpty: 'No hay grupos para filtrar.',
      name: 'Nombre',
      sourceConnection: 'Conexión de origen',
      type: 'Tipo',
      description: 'Descripción',
      audiences: 'Audiencias',
      activeExports: 'Exportaciones activas',
      syncs: 'Sincronizaciones',
      groups: 'Grupos',
      status: 'Estado',
      lastUpdated: 'Última actualización',
      createdAt: 'Fecha de creación',
      options: 'Opciones',
      edit: 'Editar conjunto de datos',
      refreshFields: 'Actualizar campos',
      delete: 'Eliminar conjunto de datos',
      empty: 'No hay conjuntos de datos.',
      emptySearch: 'No hay conjuntos de datos que coincidan con tu búsqueda.',
      loadFailed: 'No se pudieron cargar los conjuntos de datos.',
      tryAgain: 'Reintentar',
      emptyValue: '-',
      deleteTitle: '¿Eliminar conjunto de datos?',
      deleteDescription: (name: string) =>
        `Esto elimina permanentemente “${name}”.`,
      deleteConfirm: 'Eliminar',
      deleteCancel: 'Cancelar',
      deleteSuccess: 'Conjunto de datos eliminado',
      deleteFailed: 'No se pudo eliminar el conjunto de datos.',
      refreshSuccess: (name: string) =>
        `Caché del conjunto de datos limpiada para “${name}.”`,
      refreshFailed: 'No se pudo limpiar la caché del conjunto de datos.',
      lastUpdatedUnavailable: 'no disponible',
      lastUpdatedView: 'no aplica para vistas',
      lastUpdatedViewTooltip:
        'Este conjunto de datos es una vista y lee en vivo de sus tablas de origen. La última actualización solo se muestra para tablas base y vistas materializadas.',
    },
    sourceConnectionsTable: {
      ariaLabel: 'Tabla de conexiones de origen',
      search: 'Buscar conexiones de origen',
      id: 'Id',
      name: 'Nombre',
      type: 'Tipo',
      description: 'Descripción',
      teams: 'Equipos',
      datasetGroups: 'Grupos de conjuntos de datos',
      lastSchemaRefresh: 'Última actualización del esquema',
      schemaRefreshInProgress: 'Actualización del esquema en curso',
      schemaRefreshFailed: 'Falló la última actualización del esquema',
      schemaRefreshTrigger: 'Iniciar la sincronización del esquema',
      schemaRefreshTriggered:
        'Hemos iniciado la sincronización del esquema. Puede tardar unos minutos',
      schemaRefreshSubmitted: 'Actualización de esquema enviada',
      schemaRefreshSubmitFailed:
        'No se pudo iniciar la actualización del esquema.',
      status: 'Estado',
      active: 'Activo',
      createdAt: 'Fecha de creación',
      empty: 'No hay conexiones de origen.',
      emptySearch: 'No hay conexiones que coincidan con tu búsqueda.',
      loadFailed: 'No se pudieron cargar las conexiones de origen.',
      tryAgain: 'Reintentar',
      emptyValue: '-',
    },
    queueEventsTable: {
      ariaLabel: 'Tabla de eventos en tiempo real',
      empty: 'No hay eventos en tiempo real.',
      emptySearch:
        'No hay eventos en tiempo real que coincidan con tu búsqueda.',
      loadFailed: 'No se pudieron cargar los eventos en tiempo real.',
      tryAgain: 'Reintentar',
    },
  },
};
